import React from "react";
import { createGlobalStyle } from "styled-components";

/**
 * `spotter` opt-in theme — the "Spotter" dark-slate/amber palette from the design mock
 * (`/tmp/portal-mapping-mock.html`), applied ONLY to `spotter/*` routes (wrapped there in
 * `AppRouter.tsx`), mirroring how `VaultThemeProvider` scopes the `--ab*` palette to ONLY the
 * `ab-vault/candidate-assignments` route. Deliberately a SEPARATE palette/namespace from `--ab*` —
 * these are two different customers/themes coexisting in the same app.
 *
 * Values are the mock's DARK-mode custom-property set (the app runs `color-scheme: dark` globally
 * — see `CustomerTheme.tsx` — so there is no light-mode variant to support here, unlike the mock
 * itself which supports both via `prefers-color-scheme`/`data-theme`).
 */
export const SpotterGlobalStyle = createGlobalStyle`
  /* Set on \`body\` (not a page-level wrapper div) so the theme's background covers the full
   * viewport/overscroll area regardless of page content height — a wrapper div's background only
   * ever covers its own box. Route-scoped: this style unmounts (and the rule is removed) as soon
   * as the customer navigates off a \`spotter/*\` route, so no other page is affected. */
  body {
    background: var(--spotterPaper);
  }

  :root {
    /* These now come from @cinesys/ui's ColorProvider (\`RootTokens\`, also mounted at \`:root\`)
     * instead of duplicating the hex here — ColorProvider's own neutral ramp was ported from this
     * exact palette (see its own comment), so this is a lossless alias, not a re-theme. Only the
     * tones Spotter owns exclusively (amber accent, merge-blue, header-specific shades) stay
     * hardcoded below — the shared provider deliberately doesn't carry those globally. */
    --spotterSlate: var(--dark090);
    --spotterPaper: var(--dark060);
    --spotterSurface: var(--dark100);
    --spotterRaised: var(--dark200);
    --spotterInk: var(--gray400);
    --spotterInkSoft: var(--gray200);
    --spotterInkFaint: var(--dark700);
    --spotterAccent: #e0a23a;
    --spotterAccentBright: #e0a23a;
    --spotterAccentSoft: #2a2417;
    --spotterAccentLine: #4a3d26;
    /** Select-menu row hover/focus fill only (maps to \`--selectOptionHover\` in every Spotter
     *  screen's \`Wrap\` — see \`Select.tsx\`'s \`option\` style; deliberately a separate token from
     *  \`--blue060\`, which stays full \`--spotterAccent\` and still drives the control's own focus
     *  border/ring). A translucent wash of \`--spotterAccent\` rather than the full opaque amber —
     *  that read as bright yellow slamming against the light option text, way more contrast than a
     *  hover state needs. */
    --spotterAccentHover: rgba(224, 162, 58, 0.28);
    --spotterOk: #6fbf95;
    --spotterOkSoft: var(--greenBackground);
    /** Border tone for success/"save" buttons — matches \`--spotterAccentLine\`'s muted visibility,
     *  just green instead of amber, since \`--spotterOk\`/\`--spotterOkSoft\` alone don't have a border
     *  shade of their own. */
    --spotterOkLine: var(--greenBorder);
    --spotterMerge: #8fb0da;
    --spotterMergeSoft: #1c242f;
    --spotterMergeLine: #3a4b5e;
    /** Fields tab's five-mode color coding (\`MODE_COLORS\`, \`fields/modeColors.ts\`) — one distinct
     *  tone per mode (Direct/Substitute/Format/Conditional/Glossary), used consistently on the
     *  Mode dropdown, the row header badge, and the page's intro copy. Direct reuses
     *  \`--spotterAccent\` (amber) above and Glossary gets the purple defined here.
     *  Substitute/Format/Conditional below bridge between them, all five drawn from one
     *  warm-to-cool family (amber -> coral -> pink -> magenta -> purple) so they read as one
     *  coherent set rather than an arbitrary rainbow — see \`modeColors.ts\`'s own doc comment
     *  before changing any of these five. \`--spotterMerge\` (blue) sits outside this family — it
     *  backs the separate Facial Recognition tab's fixed row badge instead
     *  (\`modeColors.ts\`'s \`FR_BADGE_COLOR\`). */
    --spotterFacial: #b592e8;
    --spotterFacialSoft: #241d33;
    --spotterFacialLine: #493a5e;
    --spotterSubstitute: #e29683;
    --spotterSubstituteSoft: #262222;
    --spotterSubstituteLine: #4c403d;
    --spotterFormat: #e283a9;
    --spotterFormatSoft: #262223;
    --spotterFormatLine: #4c3d43;
    --spotterConditional: #e283e2;
    --spotterConditionalSoft: #262226;
    --spotterConditionalLine: #4c3d4c;
    /** Danger/error red — warm and red-orange rather than a straight red so it sits next to
     *  \`--spotterAccent\`'s amber without clashing. \`--spotterDangerBorder\` is a separate tone from
     *  \`--spotterDangerSoft\`: the old single dark "soft" value doubled as both border and fill,
     *  which made danger-button borders nearly invisible against \`--spotterSurface\` and made the
     *  fill read as a flat near-black smear wherever it showed up against a lighter surface.
     *  \`--spotterDangerBorder\` is tuned to the same muted visibility as \`--spotterAccentLine\` (the
     *  "default"-type button's own border) rather than a brighter, more saturated red — an
     *  earlier, more saturated value read as too loud next to it. */
    --spotterDanger: #e2604a;
    --spotterDangerBorder: var(--redBorderOverlay);
    --spotterDangerSoft: var(--redBackground);
    --spotterLine: var(--dark300);
    --spotterLine2: var(--dark400);
    --spotterCodeBg: #20252a;
    /** Gamebook tab (follow-up to PR #210) — the play list's "FLAG" (penalty) badge. A distinct
     *  tone from \`--spotterAccent\` (the brand amber, used everywhere else) so a penalty flag
     *  doesn't read as "the same amber as every button/link" — values are the reference mock's
     *  (\`/tmp/gamebook.html\`) own dark-mode \`--flag\`/\`--flag-soft\`. Scoring plays reuse
     *  \`--spotterOk\`/\`--spotterOkSoft\` (already green) and "key play"/critical reuse
     *  \`--spotterDanger\`/\`--spotterDangerSoft\` — no new tokens needed for either. */
    --spotterFlag: #d7a94b;
    --spotterFlagSoft: #2a2413;
    /** Applied to every surface card (\`.vpick\`, \`.row\`, \`.card\`) — originally the design mock's
     *  own dark-mode \`--shadow\` verbatim, but that reached (30px blur, 10px offset) well past the
     *  ~14px padding most of these cards actually sit inside, spilling into neighboring rows/the
     *  page edge. Tightened and lightened so the shadow stays inside typical surrounding padding
     *  while still reading as "raised". */
    --spotterShadow: 0 1px 2px rgba(0, 0, 0, 0.22), 0 3px 8px rgba(0, 0, 0, 0.22);

    /* Header-specific tones (mock's inline header colors, not part of its :root palette). */
    --spotterHeaderMuted: var(--headerCrumbMuted);
    --spotterHeaderCrumbStrong: var(--headerCrumbStrong);
    --spotterHeaderBarTrack: #2c343c;
  }
`;

interface Props {
  children?: React.ReactNode;
}

const SpotterThemeProvider = ({ children }: Props) => (
  <>
    <SpotterGlobalStyle />
    {children}
  </>
);

export default SpotterThemeProvider;
